import userConfigurationDictionaryObject from 'owa-service/lib/factory/userConfigurationDictionaryObject';
import updateUserConfiguration from 'owa-session-store/lib/services/updateUserConfiguration';
import userConfigurationDictionaryEntry from 'owa-service/lib/factory/userConfigurationDictionaryEntry';
import { updateInboxTabs } from '../actions/updateInboxTabs';
import { createLazyOrchestrator } from 'owa-bundling';
import { setIsFocusedInboxCapableService } from 'owa-mail-focused-inbox-config/lib/services/setIsFocusedCapableService';
import getUserConfigurationTabFromInboxClassificationFilter from '../selectors/getUserConfigurationTabFromInboxClassificationFilter';
import { updateInboxTabsWatermarks } from '../actions/updateInboxTabsWatermarks';
import { orchestrator } from 'satcheljs';
import { onInboxPivotSelected } from '../actions/onInboxPivotSelected';
import { getUserConfiguration } from 'owa-session-store';
import UserConfigurationDictionaryEntry from 'owa-service/lib/contract/UserConfigurationDictionaryEntry';
import {
    bootstrapInboxPlusPivots,
    onPivotEnableDisableDialogSave,
} from 'owa-mail-inbox-plus-store';
import InboxPlusPivotViewState from 'owa-mail-inbox-plus-store/lib/store/schema/InboxPlusPivotViewState';
import InboxClassificationFilter from 'owa-mail-inbox-plus-store/lib/store/schema/InboxClassificationFilter';
import inboxPlusStore from 'owa-mail-inbox-plus-store/lib/store/Store';

export const updateUserConfigValuesOnBootstrap = createLazyOrchestrator(
    bootstrapInboxPlusPivots,
    'clone_bootstrapInboxPlusPivots',
    actionMessage => {
        const enabledPivots = actionMessage.pivotsToSet.filter(pivot => pivot.isEnabled);

        if (actionMessage.isFirstRunExperience) {
            // For first run experience, only update the server values since we do not
            // want to show the Inbox plus UI in the first session due to invalid session data
            const watermarksToSet = getCurrentTimeWatermarks(enabledPivots.length);

            updateUserConfiguration('OWA.UserOptions', [
                userConfigurationDictionaryEntry({
                    DictionaryKey: userConfigurationDictionaryObject({
                        Type: 'String',
                        Value: ['InboxPlusTabs'],
                    }),
                    DictionaryValue: userConfigurationDictionaryObject({
                        Type: 'StringArray',
                        Value: getUserConfigPivots(enabledPivots),
                    }),
                }),
                getInboxPlusWatermarksUserConfigEntry(watermarksToSet),
            ]);
        } else if (actionMessage.fixInboxPlusState) {
            // If the pivots had to be reset to a valid state, we need to update local and server user configuration values
            setInboxTabsWatermarksAndFocusedEnabled(enabledPivots);
        } else {
            // Otherwise this is a normal boot into inbox plus. Set the leftmost pivot's watermark to current time since it's selected on boot
            const watermarks = getUserConfiguration().UserOptions.InboxPlusWatermarks.slice();
            watermarks[0] = new Date(Date.now()).toUTCString();
            updateUserConfigWatermarks(watermarks);
        }
    }
);

export const updateUserConfigValuesOnDialogSave = createLazyOrchestrator(
    onPivotEnableDisableDialogSave,
    'clone_onPivotEnableDisableDialogSave',
    actionMessage => {
        setInboxTabsWatermarksAndFocusedEnabled(actionMessage.pivotsToSet);
    }
);

orchestrator(onInboxPivotSelected, actionMessage => {
    const enabledPivots = inboxPlusStore.inboxPlusPivots
        .filter(pivot => pivot.isEnabled)
        .map(pivot => pivot.InboxClassificationFilter);
    const currentPivotWatermarkIndex = enabledPivots.indexOf(actionMessage.currentPivot);
    const pivotToSelectWatermarkIndex = enabledPivots.indexOf(actionMessage.pivotToSelect);

    const watermarks = getUserConfiguration().UserOptions.InboxPlusWatermarks.slice();
    const nowDate = new Date(Date.now()).toUTCString();

    watermarks[pivotToSelectWatermarkIndex] = nowDate;
    watermarks[currentPivotWatermarkIndex] = nowDate;

    updateUserConfigWatermarks(watermarks);
});

function updateUserConfigWatermarks(watermarksToSet: string[]) {
    updateInboxTabsWatermarks(watermarksToSet);

    updateUserConfiguration('OWA.UserOptions', [
        getInboxPlusWatermarksUserConfigEntry(watermarksToSet),
    ]);
}

export function getCurrentTimeWatermarks(watermarkLength: number) {
    const watermarks = [];
    const currentTime = new Date(Date.now()).toUTCString();

    for (let i = 0; i < watermarkLength; i++) {
        watermarks.push(currentTime);
    }

    return watermarks;
}

function getUserConfigPivots(pivots: InboxPlusPivotViewState[]): string[] {
    const enabledPivots = pivots.filter(pivot => pivot.isEnabled);
    const userConfigPivots = enabledPivots.map(pivot =>
        getUserConfigurationTabFromInboxClassificationFilter(pivot.InboxClassificationFilter)
    );
    return userConfigPivots;
}

function setInboxTabsWatermarksAndFocusedEnabled(pivotsToSet: InboxPlusPivotViewState[]) {
    const userConfigPivots = getUserConfigPivots(pivotsToSet);
    const watermarksToSet = getCurrentTimeWatermarks(pivotsToSet.length);

    const shouldEnableFocusedOther =
        pivotsToSet.filter(
            pivot =>
                pivot.InboxClassificationFilter === InboxClassificationFilter.Focused &&
                pivot.isEnabled
        ).length > 0;

    // Make a request to set IsFocusedInboxCapable if IsFocusedInboxEnabled is set to true
    if (shouldEnableFocusedOther && !getUserConfiguration().UserOptions.IsFocusedInboxCapable) {
        setIsFocusedInboxCapableService();
    }

    // Update local user config values
    updateInboxTabs(userConfigPivots);
    updateInboxTabsWatermarks(watermarksToSet);

    updateUserConfiguration('OWA.UserOptions', [
        userConfigurationDictionaryEntry({
            DictionaryKey: userConfigurationDictionaryObject({
                Type: 'String',
                Value: ['InboxPlusTabs'],
            }),
            DictionaryValue: userConfigurationDictionaryObject({
                Type: 'StringArray',
                Value: userConfigPivots,
            }),
        }),
        userConfigurationDictionaryEntry({
            DictionaryKey: userConfigurationDictionaryObject({
                Type: 'String',
                Value: ['IsFocusedInboxEnabled'],
            }),
            DictionaryValue: userConfigurationDictionaryObject({
                Type: 'Boolean',
                Value: [String(shouldEnableFocusedOther)],
            }),
        }),
        getInboxPlusWatermarksUserConfigEntry(watermarksToSet),
    ]);
}

function getInboxPlusWatermarksUserConfigEntry(
    watermarksToSet: string[]
): UserConfigurationDictionaryEntry {
    return userConfigurationDictionaryEntry({
        DictionaryKey: userConfigurationDictionaryObject({
            Type: 'String',
            Value: ['InboxPlusWatermarks'],
        }),
        DictionaryValue: userConfigurationDictionaryObject({
            Type: 'StringArray',
            Value: watermarksToSet,
        }),
    });
}
